import {ActionIcon, createStyles, Group, Tooltip} from '@mantine/core';
import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ExportActions from 'exports/ExportActions';
import {BasicInformationPanelState} from 'FileStates';
import TemplateCollectionActions from 'notebook/entry-templates/collections/TemplateCollectionActions';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import CreateEntryFromDraftTemplateButton from 'notebook/entry-templates/CreateEntryFromDraftTemplateButton';
import CreateEntryFromTemplateButton from 'notebook/entry-templates/CreateEntryFromTemplateButton';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import {TemplateOrSubtemplateType} from 'notebook/entry-templates/TemplateConstants';
import TemplateExportButton from 'notebook/entry-templates/TemplateExportButton';
import notifications from 'notifications';
import ReactPanelBaseView, {getTabAndPanelIds, Subpanel} from 'toolbar/ReactPanelBaseView';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';

import {TemplateCollectionGraphQL} from '../../../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';

const SHOW_CONTAINING_COLLECTION_LABEL = 'Show containing collection';

const useShowContainingCollectionButtonStyles = createStyles((theme) => ({
  button: {
    color: theme.colors.blue[6],
    '&:hover': {
      color: theme.colors.blue[8],
    },
  },
}));

/**
 * Split out from the subpanel below because `createStyles` is a hook and the subpanel is a class
 * component.
 */
function ShowContainingCollectionButton({onClick}: {onClick: () => void}): JSX.Element {
  const {classes} = useShowContainingCollectionButtonStyles();

  return (
    <Tooltip label={SHOW_CONTAINING_COLLECTION_LABEL}>
      <ActionIcon aria-label={SHOW_CONTAINING_COLLECTION_LABEL} className={classes.button} onClick={onClick}>
        <BenchlingIcon icon="upload" />
      </ActionIcon>
    </Tooltip>
  );
}

interface Props {
  templateId: string;
  templateType: TemplateOrSubtemplateType;
  initialName: string;
  initialCollection: TemplateCollectionGraphQL;
  archiveRecord: ArchiveRecord | null;
  permissions: Permissions;
  /**
   * API identifier of the version currently open in the editor, or `undefined` before the version list has
   * loaded.
   */
  openVersionApiId: string | undefined;
  isViewingLatestVersion: boolean;
  saveModel: (name: string, id: string) => void;
  deleteModel: () => void;
}

interface State {
  name: string;
  collection: TemplateCollectionGraphQL;
}

export class BaseTemplateInformationSubpanel extends React.Component<Props, State> {
  override state: State = {
    name: this.props.initialName,
    collection: this.props.initialCollection,
  };

  _handleChangeCollection = (collection: TemplateCollectionGraphQL): void => {
    this.setState({collection});
  };

  saveModel = (): void => {
    this.props.saveModel(this.state.name, this.state.collection.id);
  };

  showContainingCollection = (): void => {
    TemplateCollectionActions.openCollection(this.props.initialCollection);
  };

  requestExport = (): Promise<void> => ExportActions.requestExportFile(this.props.templateId);

  _exportAuditLogs = (): void => {
    AuditActions.exportAuditLogs([this.props.templateId]);
  };

  handleDelete = async (): Promise<void> => {
    if (await runConfirmDeleteModal({typeName: 'template'})) {
      this.props.deleteModel();
    }
  };

  override render(): JSX.Element {
    const {templateType} = this.props;
    const {writable, archivable, unarchivable} = this.props.permissions;

    return (
      <form>
        <div className="form-group u-small-spacing-top">
          <Row>
            <Col md={4}>
              <h5>Name</h5>
            </Col>
            <Col md={8}>
              <input
                type="text"
                disabled={!writable || this.props.archiveRecord != null}
                value={this.state.name}
                onChange={(e) => {
                  this.setState({name: e.target.value});
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Group align="center" display="inline-flex" noWrap={true} spacing={0}>
                <h5>Collection</h5>
                {BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE && (
                  <ShowContainingCollectionButton onClick={this.showContainingCollection} />
                )}
              </Group>
            </Col>
            <Col md={8}>
              <TemplateCollectionSelector
                disabled={!writable}
                value={this.state.collection}
                onChange={this._handleChangeCollection}
              />
            </Col>
          </Row>
        </div>
        {writable && (
          <Row key="update-btn">
            <Col md={12}>
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                fullWidth={true}
                size={ButtonSize.SMALL}
                disabled={this.props.archiveRecord != null}
                onClick={this.saveModel}
                text="Update Information"
              />
            </Col>
          </Row>
        )}
        <div className="panel-divider" key="update-create-divider" />
        {templateType === ModelTypeConstants.TEMPLATE && (
          <>
            <Row>
              <Col md={12}>
                <CreateEntryFromTemplateButton
                  templateId={this.props.templateId}
                  isTemplateArchived={this.props.archiveRecord != null}
                  openVersionApiId={this.props.openVersionApiId}
                  isViewingLatestVersion={this.props.isViewingLatestVersion}
                />
              </Col>
            </Row>
            {BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING &&
              BenchlingConfig.TEMPLATE_PUBLISHING_ENABLE_CREATE_ENTRIES_FROM_DRAFT_TEMPLATES &&
              this.props.permissions.canCreateEntriesFromDraftTemplates === true && (
                <Row>
                  <Col md={12}>
                    <CreateEntryFromDraftTemplateButton
                      templateId={this.props.templateId}
                      isTemplateArchived={this.props.archiveRecord != null}
                      openVersionApiId={this.props.openVersionApiId}
                      isViewingLatestVersion={this.props.isViewingLatestVersion}
                    />
                  </Col>
                </Row>
              )}
          </>
        )}
        {/* needs archiving permissions check here */}
        {BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING ? (
          <Row key="archive-btn">
            <Col md={12}>
              <ArchiveOrUnarchiveButton
                fullWidth={true}
                isArchivable={Boolean(archivable)}
                isUnarchivable={Boolean(unarchivable)}
                item={{
                  id: this.props.templateId,
                  name: this.props.initialName,
                  archiveRecord: this.props.archiveRecord,
                }}
                onArchive={(
                  ids: Array<string>,
                  purpose: string,
                  options: ArchiveActions.BulkArchiveFilesOptions
                ) => {
                  ArchiveActions.bulkArchiveFilesWithDataMods(ids, purpose, options);
                }}
                onUnarchive={(ids: Array<string>) => {
                  ArchiveActions.bulkUnarchiveFilesWithDataMods(ids, {});
                }}
                showText={true}
              />
            </Col>
          </Row>
        ) : (
          writable &&
          BenchlingConfig.ENABLE_DELETE && (
            <Row key="delete-btn">
              <Col md={12}>
                <Button
                  buttonStyle={ButtonStyle.NEGATIVE}
                  fullWidth={true}
                  size={ButtonSize.SMALL}
                  onClick={this.handleDelete}
                  text={
                    templateType === ModelTypeConstants.SUBTEMPLATE
                      ? 'Delete sub-template'
                      : 'Delete template'
                  }
                />
              </Col>
            </Row>
          )
        )}
        <div className="panel-divider" key="archive-audit-divider" />
        {BenchlingConfig.ENABLE_TEMPLATE_EXPORTS && (
          <Row>
            <Col md={12}>
              <TemplateExportButton
                isViewingLatestVersion={this.props.isViewingLatestVersion}
                onClick={this.requestExport}
                openVersionApiId={this.props.openVersionApiId}
                templateId={this.props.templateId}
                templateType={templateType}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={12}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              fullWidth={true}
              onClick={this._exportAuditLogs}
              size={ButtonSize.SMALL}
              text={EXPORT_AUDIT_LOG_DISPLAY_STRING}
              dataTestAttrs={{
                component: 'TemplateInformationPanelView',
                element: 'exportAuditLogButton',
              }}
            />
          </Col>
        </Row>
      </form>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class TemplateInformationPanelView extends ReactPanelBaseView<BasicInformationPanelState, any> {
  saveModel = (name: string, templateCollectionId: string): void => {
    TemplateActions.saveFileData(this.model.id, {
      data: {name, templateCollectionId},
      success: this.onSaveSuccess,
    });
  };

  onSaveSuccess = (): void => {
    notifications.success('Template successfully updated.');
  };

  deleteModel = (): void => {
    TemplateActions.delete(this.model.id, this.model.templateCollection().id);
  };

  getSubpanels = (): Array<Subpanel> => [
    {
      ...getTabAndPanelIds('information'),
      title: 'Information',
      component: (
        <BaseTemplateInformationSubpanel
          templateId={this.model.id}
          templateType={ModelTypeConstants.TEMPLATE}
          initialName={this.model.name()}
          initialCollection={this.model.templateCollection()}
          archiveRecord={this.model.archiveRecord()}
          permissions={this.model.permissions()}
          openVersionApiId={this.model.openVersionApiId()}
          isViewingLatestVersion={this.model.isLatest()}
          saveModel={this.saveModel}
          deleteModel={this.deleteModel}
        />
      ),
    },
  ];
}

export default TemplateInformationPanelView;
